import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * /config — app_config editor. Each row picks its editor from the
 * known-keys registry (see below).
 *
 * Two modes, by URL query flag:
 *   - Normal (no `advanced` param): only KNOWN keys render, no raw-key
 *     chips, no "add key" card, short caution banner. For Sam.
 *   - Advanced (`?advanced=1`): every row renders — including unknown
 *     keys with free-text editors — the raw technical key is shown
 *     next to the friendly label, and the "add key" card is visible.
 *     Same pattern as the dashboard's `?demo=1`.
 *
 * Typed editors + per-row "שמור" are identical across both modes.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Empty, Form, Input, InputNumber, Select, Space, Switch, Table, Tag, Tooltip, Typography, } from 'antd';
import { InfoCircleOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { CONFIG_KEY_SPECS, formatPercentForStorage, validateConfigValue, } from '@matematikly/shared';
import { listConfig, putConfig } from '../api';
import { PageHeader } from '../components/PageHeader';
import { notifyError, notifySuccess } from '../components/errors';
const { Text } = Typography;
const KNOWN_KEY_DISPLAY = {
    clip_watched_coverage_ratio: {
        label: 'אחוז כיסוי לסימון קליפ כנצפה',
        description: 'אחוז מהסרטון שהתלמיד חייב לצפות בו, בעמדות שונות ולא בסך הכל, כדי שהקליפ ייחשב כנצפה.',
    },
    anonymous_daily_view_limit: {
        label: 'מגבלת צפיות יומית לאנונימי',
        description: 'מספר הצפיות המותרות ליום למכשיר שאינו רשום למערכת.',
    },
    enforce_purchases: {
        label: 'אכיפת רכישות',
        description: 'כשהמתג דלוק, גישה לשיעורים בתשלום דורשת רכישה בפועל. כשהוא כבוי, כל השיעורים פתוחים, מצב פיתוח.',
    },
    struggle_upsell_fail_threshold: {
        label: 'סף כשלונות גלובלי להצעת הנחה (עקיפה לפיתוח)',
        description: 'ערך זמני לצורכי פיתוח ובדיקות. אם מוגדר ערך, הוא דורס את סף הכשלונות שנקבע בכללי ההנחה האוטומטית לכל הכיתות ולכל הרמות. במצב ייצור יש להשאיר ריק כדי להשתמש בערך שהוגדר בכלל עצמו.',
    },
};
const ADMIN_EXTRA_SPECS = {
    struggle_upsell_fail_threshold: {
        type: 'integer',
        label: KNOWN_KEY_DISPLAY.struggle_upsell_fail_threshold.label,
        description: KNOWN_KEY_DISPLAY.struggle_upsell_fail_threshold.description,
        min: 1,
        max: 50,
    },
};
function getSpec(key) {
    return CONFIG_KEY_SPECS[key] ?? ADMIN_EXTRA_SPECS[key];
}
function isKnownKey(key) {
    return key in CONFIG_KEY_SPECS || key in ADMIN_EXTRA_SPECS;
}
function displayFor(key) {
    return KNOWN_KEY_DISPLAY[key] ?? null;
}
/** Admin-side validation wrapper. For keys in the shared spec we defer to
 *  the shared validator (same rules the server runs on PUT). For
 *  admin-only known keys (`ADMIN_EXTRA_SPECS`) we validate locally with
 *  the same rules shared would use. Unknown keys pass — the server
 *  stores them as free text. */
function validate(key, value) {
    if (CONFIG_KEY_SPECS[key])
        return validateConfigValue(key, value);
    const spec = ADMIN_EXTRA_SPECS[key];
    if (!spec)
        return null;
    if (spec.type === 'integer') {
        if (!/^-?\d+$/.test(value.trim())) {
            return { code: 'config_not_integer', message: `${spec.label}: ערך חייב להיות מספר שלם` };
        }
        const n = Number(value);
        if (spec.min !== undefined && n < spec.min) {
            return { code: 'config_out_of_range', message: `${spec.label}: מינימום ${spec.min}` };
        }
        if (spec.max !== undefined && n > spec.max) {
            return { code: 'config_out_of_range', message: `${spec.label}: מקסימום ${spec.max}` };
        }
    }
    if (spec.type === 'boolean') {
        if (value !== 'true' && value !== 'false') {
            return { code: 'config_not_boolean', message: `${spec.label}: ערך חייב להיות true או false` };
        }
    }
    return null;
}
// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------
export default function ConfigScreen() {
    const [searchParams] = useSearchParams();
    const advanced = searchParams.get('advanced') === '1';
    const [advTagVisible, setAdvTagVisible] = useState(true);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const reload = useCallback(async () => {
        setLoading(true);
        try {
            setRows(await listConfig());
        }
        catch { /* client auto-surfaces */ }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { void reload(); }, [reload]);
    // Normal mode: hide any row whose key isn't in the known registry.
    const visibleRows = useMemo(() => (advanced ? rows : rows.filter((r) => isKnownKey(r.config_key))), [rows, advanced]);
    const cols = [
        {
            title: 'הגדרה', dataIndex: 'config_key', width: 320,
            render: (v) => {
                const d = displayFor(v);
                const spec = getSpec(v);
                const label = d?.label ?? spec?.label ?? v;
                return (_jsxs(Space, { direction: "vertical", size: 2, children: [_jsx(Text, { strong: true, children: label }), advanced && _jsx(Text, { type: "secondary", style: { fontSize: 12 }, code: true, children: v })] }));
            },
        },
        { title: 'ערך', render: (_, r) => _jsx(EditableValue, { row: r, onSaved: reload }) },
        {
            title: 'תיאור',
            render: (_, r) => {
                // Admin override wins over the server-provided description
                // (which may be English from the seed).
                const d = displayFor(r.config_key);
                const spec = getSpec(r.config_key);
                return d?.description ?? spec?.description ?? r.description;
            },
        },
    ];
    const bannerText = advanced
        ? 'שינויים כאן תופסים בזמן אמת במובייל בלי דיפלוי. מפתחות מוכרים מקבלים עורך ייעודי. מפתחות לא מוכרים נופלים לעריכת טקסט חופשית.'
        : 'שינויים כאן חלים מיד על האפליקציה לכל התלמידים.';
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: "\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA \u05DE\u05E2\u05E8\u05DB\u05EA", breadcrumb: [{ label: 'ניהול' }, 'הגדרות'], actions: advanced && advTagVisible ? (_jsx(Tag, { closable: true, onClose: () => setAdvTagVisible(false), children: "\u05DE\u05E6\u05D1 \u05DE\u05EA\u05E7\u05D3\u05DD" })) : undefined }), _jsx(Alert, { type: "info", showIcon: true, style: { marginBottom: 16 }, message: bannerText }), _jsx(Card, { style: { marginBottom: 16 }, children: visibleRows.length === 0 && !loading
                    ? _jsx(Empty, { description: "\u05D0\u05D9\u05DF \u05E2\u05D3\u05D9\u05D9\u05DF \u05D4\u05D2\u05D3\u05E8\u05D5\u05EA", image: Empty.PRESENTED_IMAGE_SIMPLE })
                    : _jsx(Table, { size: "small", rowKey: "config_key", pagination: false, loading: loading, dataSource: visibleRows, columns: cols }) }), advanced && _jsx(AddKey, { onDone: reload })] }));
}
// ---------------------------------------------------------------------------
// Row editor — picks a widget based on the key's spec.
// ---------------------------------------------------------------------------
function EditableValue({ row, onSaved }) {
    const spec = getSpec(row.config_key);
    const [value, setValue] = useState(row.config_value);
    const [saving, setSaving] = useState(false);
    useEffect(() => setValue(row.config_value), [row.config_value]);
    const changed = value !== row.config_value;
    const validationErr = useMemo(() => validate(row.config_key, value), [row.config_key, value]);
    const save = async () => {
        if (validationErr) {
            notifyError({ message: validationErr.message });
            return;
        }
        setSaving(true);
        try {
            await putConfig(row.config_key, value, row.description);
            notifySuccess('נשמר');
            onSaved();
        }
        catch { /* client auto-surfaces */ }
        finally {
            setSaving(false);
        }
    };
    return (_jsxs(Space, { direction: "vertical", size: 4, style: { width: '100%' }, children: [_jsxs(Space.Compact, { style: { width: '100%' }, children: [_jsx(ValueWidget, { spec: spec, value: value, onChange: setValue }), _jsx(Button, { icon: _jsx(SaveOutlined, {}), type: changed ? 'primary' : 'default', loading: saving, disabled: !changed || validationErr != null, onClick: save, children: "\u05E9\u05DE\u05D5\u05E8" })] }), validationErr && changed && (_jsx(Text, { type: "danger", style: { fontSize: 12 }, children: validationErr.message })), !validationErr && spec && (_jsx(Text, { type: "secondary", style: { fontSize: 12 }, children: _jsx(RangeHint, { spec: spec }) }))] }));
}
function ValueWidget({ spec, value, onChange }) {
    if (spec?.type === 'percent') {
        // 5% grid options within the spec's range. `formatPercentForStorage`
        // keeps the DB representation clean ("0.5" / "0.55" / "0.9"), matching
        // the pre-existing seed style — no cosmetic reformatting of every row.
        const steps = [];
        for (let n = spec.min; n <= spec.max + 1e-9; n += spec.step) {
            steps.push(formatPercentForStorage(n));
        }
        return (_jsx(Select, { style: { width: '100%' }, value: value, onChange: onChange, options: steps.map((s) => ({ value: s, label: `${Math.round(Number(s) * 100)}%` })), 
            // Fall back to a visible tag if the current DB value isn't on the
            // 5% grid (e.g. an old row with 0.73). The Select still lets Sam
            // pick a fresh valid value; save is gated by validateConfigValue.
            placeholder: value }));
    }
    if (spec?.type === 'integer') {
        return (_jsx(InputNumber, { style: { width: '100%' }, min: spec.min, max: spec.max, step: 1, value: value === '' ? undefined : Number(value), onChange: (v) => onChange(v == null ? '' : String(Math.trunc(Number(v)))) }));
    }
    if (spec?.type === 'boolean') {
        // Switches are compact but easy to miss without a text label — pair
        // with a badge so the current state is unambiguous.
        return (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '4px 8px' }, children: [_jsx(Switch, { checked: value === 'true', onChange: (next) => onChange(next ? 'true' : 'false') }), _jsx(Tag, { color: value === 'true' ? 'green' : 'default', children: value === 'true' ? 'דלוק' : 'כבוי' })] }));
    }
    // Free-text fallback — unknown key (only reachable in advanced mode).
    return (_jsx(Input, { value: value, onChange: (e) => onChange(e.target.value), suffix: _jsx(Tooltip, { title: "\u05DE\u05E4\u05EA\u05D7 \u05DC\u05D0 \u05DE\u05D5\u05DB\u05E8. \u05E2\u05E8\u05D9\u05DB\u05EA \u05D8\u05E7\u05E1\u05D8 \u05D7\u05D5\u05E4\u05E9\u05D9\u05EA \u05DC\u05DC\u05D0 \u05D1\u05D3\u05D9\u05E7\u05EA \u05E1\u05D5\u05D2.", children: _jsx(InfoCircleOutlined, { style: { color: '#8c8c8c' } }) }) }));
}
function RangeHint({ spec }) {
    if (spec.type === 'percent') {
        return _jsxs(_Fragment, { children: ["\u05D8\u05D5\u05D5\u05D7: ", pct(spec.min), "%\u2013", pct(spec.max), "% \u00B7 \u05E7\u05E4\u05D9\u05E6\u05D5\u05EA \u05E9\u05DC ", pct(spec.step), "%"] });
    }
    if (spec.type === 'integer') {
        const bounds = [
            spec.min !== undefined ? `מ־${spec.min}` : null,
            spec.max !== undefined ? `עד ${spec.max}` : null,
        ].filter(Boolean).join(' ');
        return _jsxs(_Fragment, { children: ["\u05DE\u05E1\u05E4\u05E8 \u05E9\u05DC\u05DD", bounds ? ` (${bounds})` : ''] });
    }
    return _jsx(_Fragment, { children: "\u05D1\u05D5\u05DC\u05D9\u05D0\u05E0\u05D9" });
}
function pct(n) {
    return Math.round(n * 100);
}
// ---------------------------------------------------------------------------
// Free-form "add key" — advanced mode only. Same POST endpoint; server
// accepts unknown keys as free-text.
// ---------------------------------------------------------------------------
function AddKey({ onDone }) {
    const [form] = Form.useForm();
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        let v;
        try {
            v = await form.validateFields();
        }
        catch {
            return;
        }
        setBusy(true);
        try {
            await putConfig(v.key.trim(), v.value, v.description?.trim() || null);
            notifySuccess('נשמר');
            form.resetFields();
            onDone();
        }
        catch { /* client auto-surfaces */ }
        finally {
            setBusy(false);
        }
    };
    return (_jsx(Card, { size: "small", title: "\u05D4\u05D5\u05E1\u05E3 \u05DE\u05E4\u05EA\u05D7 \u05D7\u05D3\u05E9", children: _jsxs(Form, { form: form, layout: "inline", onFinish: submit, children: [_jsx(Form.Item, { name: "key", rules: [{ required: true, message: 'מפתח נדרש' }], children: _jsx(Input, { placeholder: "\u05DE\u05E4\u05EA\u05D7", style: { width: 240 } }) }), _jsx(Form.Item, { name: "value", rules: [{ required: true, message: 'ערך נדרש' }], children: _jsx(Input, { placeholder: "\u05E2\u05E8\u05DA", style: { width: 200 } }) }), _jsx(Form.Item, { name: "description", children: _jsx(Input, { placeholder: "\u05EA\u05D9\u05D0\u05D5\u05E8 (\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9)", style: { width: 240 } }) }), _jsx(Form.Item, { children: _jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), htmlType: "submit", loading: busy, children: "\u05D4\u05D5\u05E1\u05E3" }) })] }) }));
}
